import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, InputNumber, Select, message, Space, Input, Col, Row } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { financeService, SKUCostConfig } from '../../services/financeService';

const SKUCostConfigPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<SKUCostConfig[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState<Partial<SKUCostConfig> | null>(null);
    const [form] = Form.useForm();

    // 搜索过滤
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await financeService.getCostConfigs();
            setData(res.data.results || []);
        } catch (error) {
            message.error('加载配置失败');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (record: SKUCostConfig) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleAdd = () => {
        setEditingItem(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (editingItem && editingItem.id) {
                await financeService.updateCostConfig(editingItem.id, values);
                message.success('更新成功');
            } else {
                await financeService.createCostConfig(values);
                message.success('创建成功');
            }
            setIsModalVisible(false);
            fetchData();
        } catch (error) {
            message.error('保存失败');
        }
    };

    const columns = [
        { title: 'SKU', dataIndex: 'seller_sku_code', key: 'sku' },
        { title: '产品名称', dataIndex: 'seller_sku_name', key: 'name' },
        {
            title: '采购成本',
            dataIndex: 'purchase_cost',
            key: 'purchase',
            render: (_: any, r: SKUCostConfig) => `${r.purchase_cost} ${r.purchase_currency}`
        },
        { title: '头程运费/件', dataIndex: 'shipping_to_fba', key: 'shipping' },
        { title: '包装/件', dataIndex: 'packaging_cost', key: 'packaging' },
        { title: '其他/件', dataIndex: 'other_cost', key: 'other' },
        {
            title: '总成本(USD)',
            dataIndex: 'total_cost_usd',
            key: 'total',
            render: (val: number) => `$${Number(val).toFixed(2)}`
        },
        { title: '更新时间', dataIndex: 'updated_at', key: 'updated' },
        {
            title: '操作',
            key: 'action',
            render: (_: any, record: SKUCostConfig) => (
                <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                    <Input.Search
                        placeholder="搜索SKU..."
                        onSearch={val => setSearchText(val)}
                        style={{ width: 200 }}
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        新增配置
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={data.filter(item => !searchText || item.seller_sku_code.includes(searchText))}
                rowKey="id"
                loading={loading}
            />

            <Modal
                title={editingItem ? "编辑成本配置" : "新增成本配置"}
                open={isModalVisible}
                onOk={handleSave}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    {!editingItem && (
                        <Form.Item name="seller_sku" label="Seller SKU ID" rules={[{ required: true }]}>
                            <InputNumber style={{ width: '100%' }} placeholder="输入SKU ID (临时)" />
                        </Form.Item>
                    )}
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="purchase_cost" label="采购单价" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="purchase_currency" label="币种">
                                <Select>
                                    <Select.Option value="CNY">CNY</Select.Option>
                                    <Select.Option value="USD">USD</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="shipping_to_fba" label="头程运费/件">
                                <InputNumber style={{ width: '100%' }} precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="exchange_rate_to_usd" label="汇率(对USD)">
                                <InputNumber style={{ width: '100%' }} precision={4} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="packaging_cost" label="包装成本/件">
                        <InputNumber style={{ width: '100%' }} precision={2} />
                    </Form.Item>
                    <Form.Item name="other_cost" label="其他成本/件">
                        <InputNumber style={{ width: '100%' }} precision={2} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="estimated_fba_fee" label="预估FBA配送费/件">
                                <InputNumber style={{ width: '100%' }} precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="referral_fee_rate" label="预估佣金比率 (0.15=15%)">
                                <InputNumber style={{ width: '100%' }} precision={4} step={0.01} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="notes" label="备注">
                        <Input.TextArea />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SKUCostConfigPage;
